import { zodResolver } from '@hookform/resolvers/zod'
import {
    AlertCircle,
    Check,
    ChevronRight,
    Download,
    Edit2,
    MapPin,
    Package,
    Plus,
    Trash2,
    Upload,
    ToggleLeft,
    ToggleRight,
    X,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z from 'zod'

import { Spinner } from '@/app/lib/loader/loader'
import {
    shippingCoverageService,
    type ShippingCoverage,
    type ShippingCoverageRule,
} from '@/app/services/shipping-coverage.service'
import { useSessionStore } from '@/app/store/auth/auth.store'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { ConfirmationModal } from '../ui/ConfirmationModal'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog'
import { FormError } from '../ui/FormError'
import { Input } from '../ui/input'
import type { Sucursal } from '@/app/services/sucursal.service'

// ─── Schemas ────────────────────────────────────────────────────────────────

const coverageSchema = z.object({
    name: z.string().min(1, 'El nombre es requerido').max(255),
    is_active: z.boolean(),
})

const ruleSchema = z.object({
    postal_code: z
        .string()
        .regex(/^\d{5}$/, 'Debe ser de exactamente 5 dígitos numéricos'),
    minimum_purchase: z.string(),
    is_active: z.boolean(),
})

type CoverageForm = z.infer<typeof coverageSchema>
type RuleFormRaw = z.infer<typeof ruleSchema>
type RuleForm = { postal_code: string; minimum_purchase: number; is_active: boolean }

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
    sucursal: Sucursal
    open: boolean
    onClose: () => void
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ShippingCoverageModal({ sucursal, open, onClose }: Props) {
    const canCreate = useSessionStore((s) => s.canAction('sucursals.shipping.create'))
    const canEdit = useSessionStore((s) => s.canAction('sucursals.shipping.edit'))
    const canDelete = useSessionStore((s) => s.canAction('sucursals.shipping.delete'))
    const canImport = useSessionStore((s) => s.canAction('sucursals.shipping.import'))

    // ── Coberturas ────────────────────────────────────────────────────────────
    const [coverages, setCoverages] = useState<ShippingCoverage[]>([])
    const [selectedCoverage, setSelectedCoverage] = useState<ShippingCoverage | null>(null)
    const [loadingCoverages, setLoadingCoverages] = useState(false)

    const [showCoverageForm, setShowCoverageForm] = useState(false)
    const [editingCoverage, setEditingCoverage] = useState<ShippingCoverage | null>(null)
    const [deleteCoverageId, setDeleteCoverageId] = useState<number | null>(null)

    // ── Reglas ────────────────────────────────────────────────────────────────
    const [rules, setRules] = useState<ShippingCoverageRule[]>([])
    const [loadingRules, setLoadingRules] = useState(false)

    const [showRuleForm, setShowRuleForm] = useState(false)
    const [editingRule, setEditingRule] = useState<ShippingCoverageRule | null>(null)
    const [deleteRuleId, setDeleteRuleId] = useState<number | null>(null)

    // ── Importar ──────────────────────────────────────────────────────────────
    const [importing, setImporting] = useState(false)
    const importInputRef = useRef<HTMLInputElement>(null)

    // ─── Forms ────────────────────────────────────────────────────────────────

    const coverageForm = useForm<CoverageForm>({
        resolver: zodResolver(coverageSchema),
        defaultValues: { name: '', is_active: true },
    })

    const ruleForm = useForm<RuleFormRaw>({
        resolver: zodResolver(ruleSchema),
        defaultValues: { postal_code: '', minimum_purchase: '0', is_active: true },
    })

    const coverageIsActive = coverageForm.watch('is_active')
    const ruleIsActive = ruleForm.watch('is_active')

    // ─── Fetch Coberturas ─────────────────────────────────────────────────────

    const fetchCoverages = useCallback(async () => {
        setLoadingCoverages(true)
        try {
            const data = await shippingCoverageService.getCoverages(sucursal.id)
            setCoverages(data)
        } catch {
            toast.error('Error al cargar las coberturas')
        } finally {
            setLoadingCoverages(false)
        }
    }, [sucursal.id])

    // ─── Fetch Reglas ─────────────────────────────────────────────────────────

    const fetchRules = useCallback(async (coverage: ShippingCoverage) => {
        setLoadingRules(true)
        try {
            const full = await shippingCoverageService.getCoverage(sucursal.id, coverage.id)
            setRules(full.rules ?? [])
        } catch {
            toast.error('Error al cargar los códigos postales')
        } finally {
            setLoadingRules(false)
        }
    }, [sucursal.id])

    useEffect(() => {
        if (open) {
            fetchCoverages()
            setSelectedCoverage(null)
            setRules([])
        }
    }, [open, fetchCoverages])

    useEffect(() => {
        if (selectedCoverage) {
            fetchRules(selectedCoverage)
            setShowRuleForm(false)
            setEditingRule(null)
        }
    }, [selectedCoverage, fetchRules])

    // ─── Coverage Handlers ────────────────────────────────────────────────────

    const openCreateCoverage = () => {
        setEditingCoverage(null)
        coverageForm.reset({ name: '', is_active: true })
        setShowCoverageForm(true)
    }

    const openEditCoverage = (cov: ShippingCoverage) => {
        setEditingCoverage(cov)
        coverageForm.reset({ name: cov.name, is_active: cov.is_active })
        setShowCoverageForm(true)
    }

    const onSubmitCoverage = async (data: CoverageForm) => {
        try {
            if (editingCoverage) {
                const updated = await shippingCoverageService.updateCoverage(sucursal.id, editingCoverage.id, data)
                setCoverages((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)))
                if (selectedCoverage?.id === updated.id) setSelectedCoverage({ ...selectedCoverage, ...updated })
                toast.success('Cobertura actualizada')
            } else {
                const created = await shippingCoverageService.createCoverage(sucursal.id, data)
                setCoverages((prev) => [...prev, created])
                toast.success('Cobertura creada')
            }
            setShowCoverageForm(false)
            setEditingCoverage(null)
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
            toast.error(msg ?? 'Error al guardar la cobertura')
        }
    }

    const handleDeleteCoverage = async () => {
        if (!deleteCoverageId) return
        try {
            await shippingCoverageService.deleteCoverage(sucursal.id, deleteCoverageId)
            setCoverages((prev) => prev.filter((c) => c.id !== deleteCoverageId))
            if (selectedCoverage?.id === deleteCoverageId) {
                setSelectedCoverage(null)
                setRules([])
            }
            toast.success('Cobertura eliminada')
        } catch {
            toast.error('Error al eliminar la cobertura')
        } finally {
            setDeleteCoverageId(null)
        }
    }

    // ─── Rule Handlers ────────────────────────────────────────────────────────

    const openCreateRule = () => {
        setEditingRule(null)
        ruleForm.reset({ postal_code: '', minimum_purchase: '0', is_active: true })
        setShowRuleForm(true)
    }

    const openEditRule = (rule: ShippingCoverageRule) => {
        setEditingRule(rule)
        ruleForm.reset({
            postal_code: rule.postal_code,
            minimum_purchase: String(rule.minimum_purchase),
            is_active: rule.is_active,
        })
        setShowRuleForm(true)
    }

    const onSubmitRule = async (data: RuleFormRaw) => {
        if (!selectedCoverage) return
        const payload: RuleForm = {
            postal_code: data.postal_code,
            minimum_purchase: parseFloat(data.minimum_purchase ?? '0') || 0,
            is_active: data.is_active,
        }
        try {
            if (editingRule) {
                const updated = await shippingCoverageService.updateRule(
                    sucursal.id, selectedCoverage.id, editingRule.id, payload,
                )
                setRules((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
                toast.success('Regla actualizada')
            } else {
                const created = await shippingCoverageService.createRule(sucursal.id, selectedCoverage.id, payload)
                setRules((prev) => [...prev, created])
                toast.success('Código postal agregado')
            }
            setShowRuleForm(false)
            setEditingRule(null)
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
            toast.error(msg ?? 'Error al guardar la regla')
        }
    }

    const handleDeleteRule = async () => {
        if (!deleteRuleId || !selectedCoverage) return
        try {
            await shippingCoverageService.deleteRule(sucursal.id, selectedCoverage.id, deleteRuleId)
            setRules((prev) => prev.filter((r) => r.id !== deleteRuleId))
            toast.success('Código postal eliminado')
        } catch {
            toast.error('Error al eliminar la regla')
        } finally {
            setDeleteRuleId(null)
        }
    }

    // ─── Import Handler ───────────────────────────────────────────────────────

    const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        setImporting(true)
        try {
            const result = await shippingCoverageService.importCoverages(sucursal.id, file)
            toast.success(
                `Importación completa: ${result.created} creados, ${result.updated} actualizados, ${result.skipped} omitidos.`,
            )
            if (Object.keys(result.errors).length > 0) {
                const firstErrors = Object.values(result.errors).slice(0, 3).join(' | ')
                toast.warning(`Errores: ${firstErrors}`)
            }
            await fetchCoverages()
            if (selectedCoverage) await fetchRules(selectedCoverage)
        } catch {
            toast.error('Error al importar el archivo')
        } finally {
            setImporting(false)
            if (importInputRef.current) importInputRef.current.value = ''
        }
    }

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
                <DialogContent className="bg-popover sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader className="shrink-0">
                        <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-popover-foreground">
                            <Package className="h-5 w-5 text-primary" />
                            Coberturas de Envío — {sucursal.name}
                        </DialogTitle>
                        <DialogDescription>
                            Gestiona las zonas de cobertura y sus códigos postales permitidos.
                        </DialogDescription>
                    </DialogHeader>

                    {/* ── Layout dos columnas ── */}
                    <div className="flex flex-1 gap-4 overflow-hidden min-h-0">

                        {/* ── Panel Izquierdo: Coberturas ── */}
                        <div className="flex w-72 shrink-0 flex-col gap-3 overflow-hidden rounded-lg border border-border bg-background p-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    Coberturas
                                </span>
                                {canCreate && (
                                    <button
                                        onClick={openCreateCoverage}
                                        title="Nueva cobertura"
                                        className="rounded p-1 text-primary transition-colors hover:bg-primary/10"
                                    >
                                        <Plus className="h-4 w-4" />
                                    </button>
                                )}
                            </div>

                            {/* Form inline crear/editar cobertura */}
                            {showCoverageForm && (
                                <form
                                    onSubmit={coverageForm.handleSubmit(onSubmitCoverage)}
                                    className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2"
                                >
                                    <p className="text-xs font-medium text-primary">
                                        {editingCoverage ? 'Editar cobertura' : 'Nueva cobertura'}
                                    </p>
                                    <Input
                                        placeholder="Nombre de la cobertura"
                                        className="h-8 text-sm"
                                        autoFocus
                                        {...coverageForm.register('name')}
                                    />
                                    <FormError message={coverageForm.formState.errors.name?.message} />

                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={coverageIsActive}
                                            onClick={() => coverageForm.setValue('is_active', !coverageIsActive)}
                                            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors ${coverageIsActive ? 'bg-primary' : 'bg-primary/20'
                                                }`}
                                        >
                                            <span
                                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-popover shadow ring-0 transition duration-200 ${coverageIsActive ? 'translate-x-2' : '-translate-x-2'
                                                    }`}
                                            />
                                        </button>
                                        <span className="text-xs text-popover-foreground">Activa</span>
                                    </div>

                                    <div className="flex gap-2">
                                        <Button
                                            type="submit"
                                            size="sm"
                                            className="h-7 flex-1 text-xs"
                                            disabled={coverageForm.formState.isSubmitting}
                                        >
                                            {coverageForm.formState.isSubmitting ? (
                                                <Spinner className="h-3 w-3" />
                                            ) : (
                                                <Check className="h-3 w-3" />
                                            )}
                                            <span className="ml-1">Guardar</span>
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            className="h-7 text-xs"
                                            onClick={() => { setShowCoverageForm(false); setEditingCoverage(null) }}
                                        >
                                            <X className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </form>
                            )}

                            {/* Lista de coberturas */}
                            <div className="flex-1 space-y-1 overflow-y-auto">
                                {loadingCoverages ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Spinner className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                ) : coverages.length === 0 ? (
                                    <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
                                        <Package className="h-8 w-8 opacity-30" />
                                        <p className="text-xs">Sin coberturas</p>
                                    </div>
                                ) : (
                                    coverages.map((cov) => (
                                        <div
                                            key={cov.id}
                                            onClick={() => setSelectedCoverage(cov)}
                                            className={`group flex cursor-pointer items-center justify-between rounded-md px-3 py-2 transition-colors ${selectedCoverage?.id === cov.id
                                                ? 'bg-primary text-primary-foreground'
                                                : 'hover:bg-muted'
                                                }`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-medium">{cov.name}</p>
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    <Badge
                                                        className={`text-[10px] px-1.5 py-0 ${selectedCoverage?.id === cov.id
                                                            ? cov.is_active
                                                                ? 'bg-white/20 text-white'
                                                                : 'bg-white/10 text-white/70'
                                                            : cov.is_active
                                                                ? 'bg-[#DCFCE6] text-[#008438]'
                                                                : 'bg-[#FFE2E2] text-[#C10008]'
                                                            }`}
                                                    >
                                                        {cov.is_active ? 'Activa' : 'Inactiva'}
                                                    </Badge>
                                                </div>
                                            </div>

                                            <div className="ml-2 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                                                {canEdit && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); openEditCoverage(cov) }}
                                                        title="Editar"
                                                        className={`rounded p-1 transition-colors ${selectedCoverage?.id === cov.id
                                                            ? 'hover:bg-white/20 text-white'
                                                            : 'hover:bg-muted-foreground/10'
                                                            }`}
                                                    >
                                                        <Edit2 className="h-3 w-3" />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setDeleteCoverageId(cov.id) }}
                                                        title="Eliminar"
                                                        className={`rounded p-1 transition-colors ${selectedCoverage?.id === cov.id
                                                            ? 'hover:bg-white/20 text-destructive-foreground'
                                                            : 'text-destructive hover:bg-destructive/10'
                                                            }`}
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </button>
                                                )}
                                            </div>

                                            <ChevronRight className={`ml-1 h-3 w-3 shrink-0 transition-transform ${selectedCoverage?.id === cov.id ? 'text-primary-foreground' : 'text-muted-foreground'
                                                }`} />
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* ── Panel Derecho: Reglas ── */}
                        <div className="flex flex-1 flex-col gap-3 overflow-hidden rounded-lg border border-border bg-background p-3">
                            {!selectedCoverage ? (
                                <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground">
                                    <MapPin className="h-10 w-10 opacity-25" />
                                    <p className="text-sm">Selecciona una cobertura para ver sus códigos postales</p>
                                </div>
                            ) : (
                                <>
                                    {/* Toolbar */}
                                    <div className="flex items-center justify-between shrink-0">
                                        <div>
                                            <p className="text-sm font-semibold text-popover-foreground">
                                                {selectedCoverage.name}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {rules.length} código{rules.length !== 1 ? 's' : ''} postal{rules.length !== 1 ? 'es' : ''}
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {/* Descargar plantilla */}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-8 gap-1.5 text-xs"
                                                onClick={() => shippingCoverageService.downloadTemplate(sucursal.id)}
                                                title="Descargar plantilla para importación masiva"
                                            >
                                                <Download className="h-3.5 w-3.5" />
                                                Plantilla
                                            </Button>

                                            {/* Importar Excel */}
                                            {canImport && (
                                                <>
                                                    <input
                                                        ref={importInputRef}
                                                        type="file"
                                                        accept=".xlsx,.xls,.csv"
                                                        className="hidden"
                                                        onChange={handleImportFile}
                                                        id="coverage-import-input"
                                                    />
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 gap-1.5 text-xs"
                                                        disabled={importing}
                                                        onClick={() => importInputRef.current?.click()}
                                                        title="Importar coberturas y CPs desde Excel/CSV"
                                                    >
                                                        {importing ? (
                                                            <Spinner className="h-3.5 w-3.5" />
                                                        ) : (
                                                            <Upload className="h-3.5 w-3.5" />
                                                        )}
                                                        Importar
                                                    </Button>
                                                </>
                                            )}

                                            {/* Nuevo CP */}
                                            {canCreate && (
                                                <Button
                                                    size="sm"
                                                    className="h-8 gap-1.5 text-xs"
                                                    onClick={openCreateRule}
                                                >
                                                    <Plus className="h-3.5 w-3.5" />
                                                    Agregar CP
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Form inline regla */}
                                    {showRuleForm && (
                                        <form
                                            onSubmit={ruleForm.handleSubmit(onSubmitRule)}
                                            className="shrink-0 rounded-md border border-primary/30 bg-primary/5 p-3 space-y-3"
                                        >
                                            <p className="text-xs font-medium text-primary">
                                                {editingRule ? 'Editar código postal' : 'Nuevo código postal'}
                                            </p>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="space-y-1">
                                                    <label className="text-xs font-medium text-popover-foreground">
                                                        Código Postal <span className="text-destructive">*</span>
                                                    </label>
                                                    <Input
                                                        placeholder="Ej. 64000"
                                                        className="h-8 text-sm"
                                                        autoFocus
                                                        maxLength={5}
                                                        {...ruleForm.register('postal_code')}
                                                    />
                                                    <FormError message={ruleForm.formState.errors.postal_code?.message} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-xs font-medium text-popover-foreground">
                                                        Compra Mínima ($)
                                                    </label>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        placeholder="0.00"
                                                        className="h-8 text-sm"
                                                        {...ruleForm.register('minimum_purchase')}
                                                    />
                                                    <FormError message={ruleForm.formState.errors.minimum_purchase?.message} />
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    role="switch"
                                                    aria-checked={ruleIsActive}
                                                    onClick={() => ruleForm.setValue('is_active', !ruleIsActive)}
                                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors ${ruleIsActive ? 'bg-primary' : 'bg-primary/20'
                                                        }`}
                                                >
                                                    <span
                                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-popover shadow ring-0 transition duration-200 ${ruleIsActive ? 'translate-x-2' : '-translate-x-2'
                                                            }`}
                                                    />
                                                </button>
                                                <span className="text-xs text-popover-foreground">Activo</span>
                                            </div>

                                            <div className="flex gap-2">
                                                <Button
                                                    type="submit"
                                                    size="sm"
                                                    className="h-7 flex-1 text-xs"
                                                    disabled={ruleForm.formState.isSubmitting}
                                                >
                                                    {ruleForm.formState.isSubmitting ? (
                                                        <Spinner className="h-3 w-3" />
                                                    ) : (
                                                        <Check className="h-3 w-3" />
                                                    )}
                                                    <span className="ml-1">Guardar</span>
                                                </Button>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-7 text-xs"
                                                    onClick={() => { setShowRuleForm(false); setEditingRule(null) }}
                                                >
                                                    <X className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        </form>
                                    )}

                                    {/* Tabla de reglas */}
                                    <div className="flex-1 overflow-auto rounded-md border border-border">
                                        {loadingRules ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Spinner className="h-6 w-6 text-muted-foreground" />
                                            </div>
                                        ) : rules.length === 0 ? (
                                            <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
                                                <MapPin className="h-8 w-8 opacity-30" />
                                                <p className="text-sm">Sin códigos postales</p>
                                                {canCreate && (
                                                    <button
                                                        onClick={openCreateRule}
                                                        className="text-xs text-primary underline-offset-2 hover:underline"
                                                    >
                                                        Agregar el primero
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <table className="w-full whitespace-nowrap">
                                                <thead className="border-b border-border bg-table-header-bg">
                                                    <tr>
                                                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                                                            Código Postal
                                                        </th>
                                                        <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                                                            Compra Mínima
                                                        </th>
                                                        <th className="px-4 py-2.5 text-center text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                                                            Estado
                                                        </th>
                                                        {(canEdit || canDelete) && (
                                                            <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                                                                Acciones
                                                            </th>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-border">
                                                    {rules.map((rule) => (
                                                        <tr key={rule.id} className="transition-colors hover:bg-table-row-hover">
                                                            <td className="px-4 py-2.5">
                                                                <div className="flex items-center gap-2">
                                                                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                                                                    <span className="font-mono text-sm font-medium text-table-foreground">
                                                                        {rule.postal_code}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-2.5 text-right text-sm text-table-foreground">
                                                                {parseFloat(rule.minimum_purchase) === 0
                                                                    ? <span className="text-muted-foreground italic text-xs">Sin mínimo</span>
                                                                    : `$${parseFloat(rule.minimum_purchase).toFixed(2)}`
                                                                }
                                                            </td>
                                                            <td className="px-4 py-2.5 text-center">
                                                                <Badge
                                                                    className={
                                                                        rule.is_active
                                                                            ? 'bg-[#DCFCE6] text-[#008438]'
                                                                            : 'bg-[#FFE2E2] text-[#C10008]'
                                                                    }
                                                                >
                                                                    {rule.is_active ? 'Activo' : 'Inactivo'}
                                                                </Badge>
                                                            </td>
                                                            {(canEdit || canDelete) && (
                                                                <td className="px-4 py-2.5 text-right">
                                                                    <div className="flex items-center justify-end gap-1">
                                                                        {canEdit && (
                                                                            <button
                                                                                onClick={() => openEditRule(rule)}
                                                                                title="Editar"
                                                                                className="rounded p-1.5 text-table-foreground transition-colors hover:bg-muted"
                                                                            >
                                                                                <Edit2 className="h-3.5 w-3.5" />
                                                                            </button>
                                                                        )}
                                                                        {canDelete && (
                                                                            <button
                                                                                onClick={() => setDeleteRuleId(rule.id)}
                                                                                title="Eliminar"
                                                                                className="rounded p-1.5 text-destructive transition-colors hover:bg-destructive/10"
                                                                            >
                                                                                <Trash2 className="h-3.5 w-3.5" />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <DialogFooter className="shrink-0 pt-2">
                        <Button variant="outline" onClick={onClose}>
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirmación eliminar cobertura */}
            <ConfirmationModal
                isOpen={deleteCoverageId !== null}
                onClose={() => setDeleteCoverageId(null)}
                onConfirm={handleDeleteCoverage}
                preset="delete"
                itemName="cobertura"
                description="¿Eliminar esta cobertura? Sus códigos postales quedarán inaccesibles. Esta acción no se puede deshacer."
            />

            {/* Confirmación eliminar regla */}
            <ConfirmationModal
                isOpen={deleteRuleId !== null}
                onClose={() => setDeleteRuleId(null)}
                onConfirm={handleDeleteRule}
                preset="delete"
                itemName="código postal"
            />
        </>
    )
}
